import { DEFAULT_LOCALE, LOCALE_STORAGE_KEY, normaliseLocaleTag, type SupportedLocale } from "./config";

export interface LocaleResolutionInput {
    /** Programme or diary locale from template settings (highest priority). */
    programmeLocale?: string | null;
    /** Persisted user preference from public.users.preferred_locale. */
    userPreferredLocale?: string | null;
    /** Browser navigator.language when running client-side. */
    navigatorLanguage?: string | null;
    /** Read from localStorage (client only); pass explicitly to keep this module pure. */
    storedLocale?: string | null;
}

/** Layered locale resolution: programme > user > localStorage > navigator > en-GB. */
export function resolveLocale(input: LocaleResolutionInput = {}): SupportedLocale {
    const programme = normaliseLocaleTag(input.programmeLocale);
    if (programme) return programme;

    const user = normaliseLocaleTag(input.userPreferredLocale);
    if (user) return user;

    const stored = normaliseLocaleTag(input.storedLocale);
    if (stored) return stored;

    const navigator = normaliseLocaleTag(input.navigatorLanguage);
    if (navigator) return navigator;

    return DEFAULT_LOCALE;
}

/** Read stored locale from localStorage (browser only). */
export function readStoredLocale(): string | null {
    if (typeof window === "undefined") return null;
    try {
        return localStorage.getItem(LOCALE_STORAGE_KEY);
    } catch {
        return null;
    }
}

/** Persist locale choice to localStorage (browser only). */
export function writeStoredLocale(locale: SupportedLocale): void {
    if (typeof window === "undefined") return;
    try {
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
        // ignore quota / private mode
    }
}
